import {
  CLIENT_EDR_SOC,
  CLIENT_MINDTIME_SECURITY_EDR,
  CLIENT_MINDTIME_SECURITY_SIEM,
  CLIENT_MINDTIME_SECURITY_XDR,
} from 'src/constants/constants';
import { MINDTIME_PATHS, SECURITY_SEGMENTS } from 'src/constants/mindtime/constants';
import {
  permissionBelongsToSubscriptionRegistry,
  registryModuleDefinesComponents,
  resolveSubscriptionModulePermission,
} from 'src/mindtime/config/subscriptionPermissionRegistry';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import {
  canAccess,
  hasGranularModuleChildEntitlements,
  isRoleOnlyEdrPermission,
  resolveIsResellerOrg,
  resolveSubscriptionAccessContext,
} from 'src/mindtime/utils/subscriptionPermissions';
import type { EndpointDetailsTab } from 'src/mindtime/pages/security/edr/endpoints/details/endpointDetailsTabs';
import { DEFAULT_ENDPOINT_DETAILS_TAB } from 'src/mindtime/pages/security/edr/endpoints/details/endpointDetailsTabs';

export const SECURITY_BASE_PATH = MINDTIME_PATHS.SECURITY;
export const SECURITY_DASHBOARD_HREF = MINDTIME_PATHS.SECURITY_DASHBOARD;
export const SECURITY_CLIENTS_HREF = MINDTIME_PATHS.SECURITY_CLIENTS;
export const SECURITY_ENDPOINTS_HREF = MINDTIME_PATHS.SECURITY_ENDPOINTS;

export const SECURITY_SOC_SEGMENT = SECURITY_SEGMENTS.SOC;
export const SECURITY_EDR_SEGMENT = SECURITY_SEGMENTS.EDR;
export const SECURITY_XDR_SEGMENT = SECURITY_SEGMENTS.XDR;
export const SECURITY_SIEM_SEGMENT = SECURITY_SEGMENTS.SIEM;
export const SECURITY_AGENTS_SEGMENT = SECURITY_SEGMENTS.AGENTS;

export const getSecurityClientDetailsHref = (clientId: string): string =>
  `${SECURITY_CLIENTS_HREF}/${encodeURIComponent(clientId)}`;

export const getSecurityEndpointDetailsHref = (
  systemWazuhAgentId: string,
  tab: EndpointDetailsTab = DEFAULT_ENDPOINT_DETAILS_TAB
): string =>
  `${SECURITY_ENDPOINTS_HREF}/${encodeURIComponent(systemWazuhAgentId)}/${tab}`;

export const getSecurityVulnerabilityDetailHref = (vulnerabilityId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/vulnerability-detection/${encodeURIComponent(vulnerabilityId)}`;

export const getSecurityFimEventDetailHref = (eventId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/file-integrity-monitoring/${encodeURIComponent(eventId)}`;

export const getSecurityMalwareDetailHref = (detectionId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/malware-detection/${encodeURIComponent(detectionId)}`;

export const getSecurityMitreDetailHref = (mitreId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/mitre-attck/${encodeURIComponent(mitreId)}`;

export const getSecurityThreatHuntingDetailHref = (findingId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/threat-hunting/${encodeURIComponent(findingId)}`;

export const getSecurityAutomatedResponseDetailHref = (recordId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/automated-response/${encodeURIComponent(recordId)}`;

export const getSecurityComplianceDetailHref = (controlId: string): string =>
  `${SECURITY_BASE_PATH}/${SECURITY_EDR_SEGMENT}/configuration-compliance/${encodeURIComponent(controlId)}`;

export const getSecurityDeployAgentHref = (): string =>
  `${SECURITY_ENDPOINTS_HREF}/deploy-agent`;

export type SecurityNavItem = {
  id: string;
  label: string;
  path: string;
  permission?: string;
};

export type SecurityNavGroup = {
  id: string;
  label: string;
  segment: string;
  permission?: string;
  /** When set, child permissions resolve from the subscription registry for this product. */
  subscriptionProductId?: string;
  children: readonly SecurityNavItem[];
};

export type SecurityPermissionChecker = (perms: string[]) => boolean;

export type SecurityNavAccessOptions = {
  subscriptionKeys?: ReadonlySet<string>;
  /** JWT/role keys for the logged-in user. */
  roleKeys?: ReadonlySet<string>;
  hasActiveSubscription?: boolean;
  /** When false, hide subscription product nav items until entitlements/roles are ready. */
  subscriptionResolved?: boolean;
  isReseller?: boolean;
  organizationTypeName?: string;
  permissionsReady?: boolean;
  /** Pass `resolveClientSubscribedAccess(...)` — not raw org `hasSubscriptions`. */
};

/** Nav: reseller → role; client subscribed → subscription only; client preview → role. */
const hasNavPermission = (
  permission: string,
  checkPermissions: SecurityPermissionChecker,
  accessOptions: SecurityNavAccessOptions = {},
): boolean => {
  const isProductPermission = permissionBelongsToSubscriptionRegistry(permission);

  if (isProductPermission) {
    if (!accessOptions.permissionsReady) return false;

    const isResellerOrg =
      resolveIsResellerOrg(accessOptions.organizationTypeName) ||
      Boolean(accessOptions.isReseller);

    const roleKeys = accessOptions.roleKeys ?? new Set<string>();

    const granted = canAccess(
      resolveSubscriptionAccessContext({
        isReseller: isResellerOrg,
        clientSubscribed: isResellerOrg ? false : Boolean(accessOptions.hasActiveSubscription),
        roleKeys,
        subscriptionKeys: accessOptions.subscriptionKeys ?? new Set<string>(),
        checkRole: checkPermissions,
      }),
      permission,
      { blockPreviewCreate: true },
    );

    if (!granted) return false;

    // Role-only modules (EDR Clients) and reseller/preview: require widget children when registered.
    // Leaf modules (e.g. SOC) only need the module `-read`.
    const useRoleChildren =
      isRoleOnlyEdrPermission(permission) ||
      isResellerOrg ||
      !accessOptions.hasActiveSubscription;

    if (useRoleChildren) {
      if (!registryModuleDefinesComponents(permission)) return true;
      return hasGranularModuleChildEntitlements(roleKeys, permission);
    }

    return true;
  }

  return checkPermissions([permission]);
};

export const getSecurityNavItemPermission = (
  item: SecurityNavItem,
  subscriptionProductId?: string,
): string | undefined =>
  resolveSubscriptionModulePermission(subscriptionProductId, item.id, item.permission);

const securityPath = (segment: string, slug: string) => `${SECURITY_BASE_PATH}/${segment}/${slug}`;

// this is the main Security navigation groups
export const SECURITY_NAV_GROUPS: readonly SecurityNavGroup[] = [
  {
    id: 'soc',
    label: 'SOC',
    segment: SECURITY_SOC_SEGMENT,
    permission: CLIENT_EDR_SOC,
    subscriptionProductId: EDR_SOLUTION_ID,
    children: [
      {
        id: 'security-operations-center',
        label: 'Security Operations Center',
        path: SECURITY_DASHBOARD_HREF,
      },
    ],
  },
  {
    id: 'endpoint-security',
    label: 'EDR',
    segment: SECURITY_EDR_SEGMENT,
    permission: CLIENT_MINDTIME_SECURITY_EDR,
    subscriptionProductId: EDR_SOLUTION_ID,
    children: [
      {
        id: 'reseller-dashboard',
        label: 'Dashboard',
        path: securityPath(SECURITY_EDR_SEGMENT, 'reseller-dashboard'),
      },
      {
        id: 'sub-org-dashboard',
        label: 'Dashboard',
        path: securityPath(SECURITY_EDR_SEGMENT, 'sub-org-dashboard'),
      },
      {
        id: 'clients',
        label: 'Clients',
        path: securityPath(SECURITY_EDR_SEGMENT, 'clients'),
      },
      {
        id: 'endpoints',
        label: 'Endpoints',
        path: securityPath(SECURITY_EDR_SEGMENT, 'endpoints'),
      },
      {
        id: 'configuration-compliance',
        label: 'Configuration Compliance',
        path: securityPath(SECURITY_EDR_SEGMENT, 'configuration-compliance'),
      },
      {
        id: 'malware-detection',
        label: 'Malware Detection',
        path: securityPath(SECURITY_EDR_SEGMENT, 'malware-detection'),
      },
      {
        id: 'automated-response',
        label: 'Automated Response',
        path: securityPath(SECURITY_EDR_SEGMENT, 'automated-response'),
      },
      {
        id: 'file-integrity-monitoring',
        label: 'File Integrity Monitoring',
        path: securityPath(SECURITY_EDR_SEGMENT, 'file-integrity-monitoring'),
      },
      {
        id: 'threat-hunting',
        label: 'Threat Hunting',
        path: securityPath(SECURITY_EDR_SEGMENT, 'threat-hunting'),
      },
      {
        id: 'vulnerability-detection',
        label: 'Vulnerability Detection',
        path: securityPath(SECURITY_EDR_SEGMENT, 'vulnerability-detection'),
      },
      {
        id: 'mitre-attck',
        label: 'MITRE ATT&CK',
        path: securityPath(SECURITY_EDR_SEGMENT, 'mitre-attck'),
      },
    ],
  },
  {
    id: 'security-operations',
    label: 'XDR',
    segment: SECURITY_XDR_SEGMENT,
    permission: CLIENT_MINDTIME_SECURITY_XDR,
    children: [
      { id: 'pci-dss', label: 'PCI DSS', path: securityPath(SECURITY_XDR_SEGMENT, 'pci-dss') },
      { id: 'gdpr', label: 'GDPR', path: securityPath(SECURITY_XDR_SEGMENT, 'gdpr') },
      { id: 'hipaa', label: 'HIPAA', path: securityPath(SECURITY_XDR_SEGMENT, 'hipaa') },
      { id: 'nist-800-53', label: 'NIST 800-53', path: securityPath(SECURITY_XDR_SEGMENT, 'nist-800-53') },
      { id: 'tsc', label: 'TSC', path: securityPath(SECURITY_XDR_SEGMENT, 'tsc') },
    ],
  },
  {
    id: 'cloud-security',
    label: 'SIEM',
    segment: SECURITY_SIEM_SEGMENT,
    permission: CLIENT_MINDTIME_SECURITY_SIEM,
    children: [
      { id: 'google-cloud', label: 'Google Cloud', path: securityPath(SECURITY_SIEM_SEGMENT, 'google-cloud') },
      { id: 'office-365', label: 'Office 365', path: securityPath(SECURITY_SIEM_SEGMENT, 'office-365') },
    ],
  },
] as const;

/** Fallback for unknown Security path segments (landing uses `getSecurityLandingPath`). */
export const SECURITY_DEFAULT_GROUP =
  SECURITY_NAV_GROUPS.find((group) => group.id === 'endpoint-security') ?? SECURITY_NAV_GROUPS[0];

export const SECURITY_NAV_ROUTE_ITEMS: readonly SecurityNavItem[] = SECURITY_NAV_GROUPS.flatMap(
  (group) => group.children
);

export const getSecurityNavItemById = (id: string): SecurityNavItem | undefined =>
  SECURITY_NAV_ROUTE_ITEMS.find((item) => item.id === id);

export const getSecurityGroupBySegment = (segment: string): SecurityNavGroup | undefined =>
  SECURITY_NAV_GROUPS.find((group) => group.segment === segment);


// Applied for permissions and work with that
export const isSecurityNavItemVisible = (
  item: SecurityNavItem,
  checkPermissions: SecurityPermissionChecker,
  isReseller: boolean,
  accessOptions: SecurityNavAccessOptions = {},
  subscriptionProductId?: string,
): boolean => {
  if (item.id === 'reseller-dashboard' && !isReseller) return false;
  if (item.id === 'sub-org-dashboard' && isReseller) return false;
  const permission = getSecurityNavItemPermission(item, subscriptionProductId);
  if (subscriptionProductId && !permission?.trim()) return false;
  if (permission && !hasNavPermission(permission, checkPermissions, accessOptions)) {
    return false;
  }
  return true;
};

export const getVisibleSecurityNavItems = (
  children: readonly SecurityNavItem[],
  checkPermissions: SecurityPermissionChecker,
  isReseller: boolean,
  accessOptions: SecurityNavAccessOptions = {},
  subscriptionProductId?: string,
): SecurityNavItem[] =>
  children.filter((item) =>
    isSecurityNavItemVisible(item, checkPermissions, isReseller, accessOptions, subscriptionProductId),
  );

export const isSecurityNavGroupVisible = (
  group: SecurityNavGroup,
  checkPermissions: SecurityPermissionChecker,
  isReseller: boolean,
  accessOptions: SecurityNavAccessOptions = {},
): boolean => {
  // Subscribed clients: EDR/SOC leaf visibility comes from org subscription module keys.
  // Resellers: require the group JWT first (Client Mindtime Security EDR / SOC), then modules.
  const skipGroupJwtPermission =
    Boolean(accessOptions.hasActiveSubscription) &&
    !Boolean(accessOptions.isReseller) &&
    (group.id === 'endpoint-security' || group.id === 'soc');

  if (
    group.permission &&
    !skipGroupJwtPermission &&
    !hasNavPermission(group.permission, checkPermissions, accessOptions)
  ) {
    return false;
  }
  return getVisibleSecurityNavItems(
    group.children,
    checkPermissions,
    isReseller,
    accessOptions,
    group.subscriptionProductId,
  ).length > 0;
};

export const hasAnyVisibleSecurityNav = (
  checkPermissions: SecurityPermissionChecker,
  isReseller: boolean,
  accessOptions: SecurityNavAccessOptions = {},
): boolean =>
  SECURITY_NAV_GROUPS.some((group) =>
    isSecurityNavGroupVisible(group, checkPermissions, isReseller, accessOptions),
  );

export const getSecurityGroupFromPathname = (pathname: string): SecurityNavGroup => {
  const normalized = pathname.replace(/\/+$/, '');
  const prefix = `${SECURITY_BASE_PATH}/`;

  if (!normalized.startsWith(prefix)) {
    return SECURITY_DEFAULT_GROUP;
  }

  const remainder = normalized.slice(prefix.length);
  const segment = remainder.split('/')[0];

  return getSecurityGroupBySegment(segment) ?? SECURITY_DEFAULT_GROUP;
};

export const getSecurityDefaultHrefForGroup = (
  group: SecurityNavGroup,
  checkPermissions: SecurityPermissionChecker,
  isReseller: boolean,
  accessOptions: SecurityNavAccessOptions = {},
): string => {
  const visible = getVisibleSecurityNavItems(
    group.children,
    checkPermissions,
    isReseller,
    accessOptions,
    group.subscriptionProductId,
  );
  if (accessOptions.hasActiveSubscription) {
    return visible[0]?.path ?? SECURITY_BASE_PATH;
  }
  return visible[0]?.path ?? group.children[0]?.path ?? SECURITY_DASHBOARD_HREF;
};

export const getSecurityLandingPath = (
  checkPermissions: SecurityPermissionChecker,
  isReseller: boolean,
  accessOptions: SecurityNavAccessOptions = {},
): string | undefined => {
  if (accessOptions.hasActiveSubscription) {
    const edrGroup = SECURITY_NAV_GROUPS.find((group) => group.id === 'endpoint-security');
    if (edrGroup) {
      const edrVisible = getVisibleSecurityNavItems(
        edrGroup.children,
        checkPermissions,
        isReseller,
        accessOptions,
        edrGroup.subscriptionProductId,
      );
      if (edrVisible[0]?.path) return edrVisible[0].path;
    }

    const socGroup = SECURITY_NAV_GROUPS.find((group) => group.id === 'soc');
    if (socGroup) {
      const socVisible = getVisibleSecurityNavItems(
        socGroup.children,
        checkPermissions,
        isReseller,
        accessOptions,
        socGroup.subscriptionProductId,
      );
      if (socVisible[0]?.path) return socVisible[0].path;
    }

    return undefined;
  }

  const group = SECURITY_NAV_GROUPS.find((g) =>
    isSecurityNavGroupVisible(g, checkPermissions, isReseller, accessOptions),
  );
  if (!group) return undefined;
  return getVisibleSecurityNavItems(
    group.children,
    checkPermissions,
    isReseller,
    accessOptions,
    group.subscriptionProductId,
  )[0]?.path;
};

export const getSecurityNavItemPath = (id: string): string | undefined =>
  getSecurityNavItemById(id)?.path;

/** Flat legacy slugs → nested route paths (relative to `mindtime/security`). */
export const SECURITY_LEGACY_ROUTE_REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  { from: 'dashboard', to: SECURITY_SOC_SEGMENT },
  { from: 'overview', to: SECURITY_SOC_SEGMENT },
  { from: `${SECURITY_EDR_SEGMENT}/soc`, to: SECURITY_SOC_SEGMENT },
  { from: 'clients', to: `${SECURITY_EDR_SEGMENT}/clients` },
  { from: 'reseller', to: `${SECURITY_EDR_SEGMENT}/clients` },
  { from: `${SECURITY_EDR_SEGMENT}/reseller`, to: `${SECURITY_EDR_SEGMENT}/clients` },
  { from: 'endpoints', to: `${SECURITY_EDR_SEGMENT}/endpoints` },
  { from: 'malware-detection', to: `${SECURITY_EDR_SEGMENT}/malware-detection` },
  { from: 'automated-response', to: `${SECURITY_EDR_SEGMENT}/automated-response` },
  { from: 'file-integrity-monitoring', to: `${SECURITY_EDR_SEGMENT}/file-integrity-monitoring` },
  { from: 'threat-hunting', to: `${SECURITY_EDR_SEGMENT}/threat-hunting` },
  { from: 'vulnerability-detection', to: `${SECURITY_EDR_SEGMENT}/vulnerability-detection` },
  { from: 'mitre-attck', to: `${SECURITY_EDR_SEGMENT}/mitre-attck` },
  { from: 'pci-dss', to: `${SECURITY_XDR_SEGMENT}/pci-dss` },
  { from: 'gdpr', to: `${SECURITY_XDR_SEGMENT}/gdpr` },
  { from: 'hipaa', to: `${SECURITY_XDR_SEGMENT}/hipaa` },
  { from: 'nist-800-53', to: `${SECURITY_XDR_SEGMENT}/nist-800-53` },
  { from: 'tsc', to: `${SECURITY_XDR_SEGMENT}/tsc` },
  { from: 'google-cloud', to: `${SECURITY_SIEM_SEGMENT}/google-cloud` },
  { from: 'office-365', to: `${SECURITY_SIEM_SEGMENT}/office-365` },
  { from: 'agents-summary', to: `${SECURITY_EDR_SEGMENT}/endpoints` },
  { from: 'deploy-agent', to: `${SECURITY_EDR_SEGMENT}/endpoints/deploy-agent` },
  { from: `${SECURITY_AGENTS_SEGMENT}/agents-summary`, to: `${SECURITY_EDR_SEGMENT}/endpoints` },
  { from: `${SECURITY_AGENTS_SEGMENT}/deploy-agent`, to: `${SECURITY_EDR_SEGMENT}/endpoints/deploy-agent` },
];

export const SECURITY_QUICK_LINK_LABELS = [
  'Quick link 1',
  'Quick link 2',
  'Quick link 3',
  'Quick link 4',
  'Quick link 5',
] as const;
